import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  RiDashboardLine,
  RiDatabase2Line,
  RiHistoryLine,
  RiBookmarkLine,
  RiTableLine,
  RiSearchLine,
  RiMoonLine,
  RiSunLine,
  RiComputerLine,
  RiLogoutBoxLine,
  RiAddLine,
  RiCodeSSlashLine,
  RiQuestionLine,
  RiSettings3Line,
  RiTeamLine,
} from '@remixicon/react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/components/theme-provider';
import { authClient } from '@/lib/auth-client';
import { api } from '@/lib/api';
import { toast } from 'sonner';

interface Database {
  id: string;
  name: string;
  type: 'postgres' | 'mysql' | 'sqlite';
}

export function CommandBar() {
  const [open, setOpen] = useState(false);
  const [databases, setDatabases] = useState<Database[]>([]);
  const navigate = useNavigate();
  const { setTheme, theme } = useTheme();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Load databases when command bar opens
  useEffect(() => {
    if (open) {
      loadDatabases();
    }
  }, [open]);

  const loadDatabases = async () => {
    try {
      const session = await authClient.getSession();
      if (session.data?.user?.id) {
        const result = await api.listDatabases(session.data.user.id);
        setDatabases(result.databases);
      }
    } catch (error) {
      console.error('Failed to load databases:', error);
    }
  };

  const handleSelect = useCallback((callback: () => void) => {
    setOpen(false);
    callback();
  }, []);

  const handleLogout = async () => {
    try {
      await authClient.signOut();
      toast.success('Signed out successfully');
      navigate({ to: '/login' });
    } catch (error) {
      toast.error('Failed to sign out');
      console.error('Logout error:', error);
    }
  };

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search commands, databases, and more..." className="h-12" />
      <CommandList>
        <CommandEmpty>
          <div className="text-center py-6">
            <RiSearchLine className="size-8 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No results found.</p>
            <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
          </div>
        </CommandEmpty>

        {/* Navigation Commands */}
        <CommandGroup heading="Navigate">
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/dashboard' }))}
            className="py-3"
          >
            <RiDashboardLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Dashboard</div>
              <div className="text-xs text-muted-foreground">Overview and analytics</div>
            </div>
            <KbdGroup>
              <Kbd>G</Kbd>
              <Kbd>D</Kbd>
            </KbdGroup>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/databases' }))}
            className="py-3"
          >
            <RiDatabase2Line className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Databases</div>
              <div className="text-xs text-muted-foreground">Manage connections</div>
            </div>
            <KbdGroup>
              <Kbd>G</Kbd>
              <Kbd>B</Kbd>
            </KbdGroup>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/saved-queries' }))}
            className="py-3"
          >
            <RiBookmarkLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Saved Queries</div>
              <div className="text-xs text-muted-foreground">Your saved SQL queries</div>
            </div>
            <KbdGroup>
              <Kbd>G</Kbd>
              <Kbd>S</Kbd>
            </KbdGroup>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/history' }))}
            className="py-3"
          >
            <RiHistoryLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Query History</div>
              <div className="text-xs text-muted-foreground">Recent executions</div>
            </div>
            <KbdGroup>
              <Kbd>G</Kbd>
              <Kbd>H</Kbd>
            </KbdGroup>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/data-browser' }))}
            className="py-3"
          >
            <RiTableLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Data Browser</div>
              <div className="text-xs text-muted-foreground">Browse table data</div>
            </div>
            <KbdGroup>
              <Kbd>G</Kbd>
              <Kbd>T</Kbd>
            </KbdGroup>
          </CommandItem>
        </CommandGroup>

        {/* Database Commands */}
        {databases.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Databases">
              {databases.map((db) => (
                <CommandItem
                  key={db.id}
                  onSelect={() =>
                    handleSelect(() => navigate({ to: `/query/${db.id}` }))
                  }
                  className="py-3"
                >
                  <RiDatabase2Line className="mr-3 size-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-normal">{db.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {db.type} database
                    </div>
                  </div>
                  <RiCodeSSlashLine className="size-4 text-muted-foreground" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Actions */}
        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/databases' }))}
            className="py-3"
          >
            <RiAddLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Add Database Connection</div>
              <div className="text-xs text-muted-foreground">Connect to a new database</div>
            </div>
            <KbdGroup>
              <Kbd>{cmdKey}</Kbd>
              <Kbd>N</Kbd>
            </KbdGroup>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/settings' }))}
            className="py-3"
          >
            <RiSettings3Line className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Settings</div>
              <div className="text-xs text-muted-foreground">Manage your preferences</div>
            </div>
            <KbdGroup>
              <Kbd>{cmdKey}</Kbd>
              <Kbd>,</Kbd>
            </KbdGroup>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate({ to: '/teams' }))}
            className="py-3"
          >
            <RiTeamLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Team Management</div>
              <div className="text-xs text-muted-foreground">Manage your team and members</div>
            </div>
          </CommandItem>
        </CommandGroup>

        {/* Theme Commands */}
        <CommandSeparator />
        <CommandGroup heading="Appearance">
          <CommandItem
            onSelect={() => handleSelect(() => setTheme('light'))}
            className="py-3"
          >
            <RiSunLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Light Mode</div>
              <div className="text-xs text-muted-foreground">Bright and clean interface</div>
            </div>
            {theme === 'light' && (
              <Badge variant="secondary" className="text-xs">Active</Badge>
            )}
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => setTheme('dark'))}
            className="py-3"
          >
            <RiMoonLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Dark Mode</div>
              <div className="text-xs text-muted-foreground">Easy on the eyes</div>
            </div>
            {theme === 'dark' && (
              <Badge variant="secondary" className="text-xs">Active</Badge>
            )}
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => setTheme('system'))}
            className="py-3"
          >
            <RiComputerLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">System</div>
              <div className="text-xs text-muted-foreground">Match your OS settings</div>
            </div>
            {theme === 'system' && (
              <Badge variant="secondary" className="text-xs">Active</Badge>
            )}
          </CommandItem>
        </CommandGroup>

        {/* Account */}
        <CommandSeparator />
        <CommandGroup heading="Account">
          <CommandItem
            onSelect={() => handleSelect(handleLogout)}
            className="py-3"
          >
            <RiLogoutBoxLine className="mr-3 size-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-normal">Sign Out</div>
              <div className="text-xs text-muted-foreground">End your session</div>
            </div>
            <KbdGroup>
              <Kbd>{cmdKey}</Kbd>
              <Kbd>Q</Kbd>
            </KbdGroup>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
